<template>
    <div>
        <div
            class="modal-backdrop fade show"
            v-if="fade && visible"
        ></div>
        <div
            v-if="visible && product"
            :id="modalId"
            :class="['modal bo-modal', 'bo-catalog-quick-add-popup']"
            tabindex="-1"
            role="dialog"
            :aria-labelledby="modalId"
            style="display: block; z-index: 1040"
        >
            <div
                :class="['success modal-dialog modal-dialog-centered modal-dialog-scrollable', 'bo-catalog-quick-add-popup']"
                role="document"
            >
                <div class="modal-content">
                    <div
                        class="modal-header"
                        :class="[{ bordered: bordered }]"
                    >
                        <div class="left-panel">
                            <slot name="header-content">
                                <div class="modal-title">{{ product?.name }}</div>
                            </slot>
                        </div>
                        <div class="right-panel">
                            <slot name="right-header"></slot>
                            <i
                                v-if="!hideCloseButton"
                                class="icon-close cursor-pointer"
                                @click="deactivate"
                            ></i>
                        </div>
                    </div>
                    <div class="modal-body">
                        <div class="item-details">
                            <div class="item-images">
                                <img
                                    :src="firstImage"
                                    alt="image"
                                />
                            </div>
                            <div class="item-info">
                                <div
                                    v-show="false"
                                    class="item-promotion-tag"
                                >
                                    <div class="promotion-tag">{{ productTranslations.promotion_ends }}</div>
                                </div>
                                <div
                                    v-if="product?.type === 'variable' || (canReadPrices && isVolumePricing)"
                                    class="item-period-code"
                                >
                                    <div
                                        v-if="canReadPrices && isVolumePricing"
                                        class="item-volume-pricing-tag"
                                    >
                                        <div class="volume-pricing-tag">{{ productTranslations.volume_pricing }}</div>
                                    </div>
                                    <template v-if="product?.type === 'variable'">
                                        <div class="item-code">
                                            {{
                                                product.selected_variation_id
                                                    ? product.getSelectedVariation().sku
                                                    : product.sku
                                            }}
                                        </div>

                                        <!-- #111887: the red fallback must wait for the inventories response — the map is
                                             empty until it lands, so the lookup misses and every product flashed a red zero -->
                                        <span
                                            v-if="
                                                !isLoadingWarehouseInventories &&
                                                !productInventoryExists(product.selected_variation_id) &&
                                                !isMatrixProduct &&
                                                showMinimalistInventory(product.getSelectedVariation())
                                            "
                                            class="item-in-stock danger"
                                        >
                                            {{ noInventoryTextHandler() }}
                                        </span>

                                        <div
                                            v-else-if="
                                                hasInventory &&
                                                !isMatrixProduct &&
                                                showMinimalistInventory(product.getSelectedVariation())
                                            "
                                            class="item-in-stock"
                                            :class="
                                                getStockClass(product.selected_variation_id, user_warehouse_inventories)
                                            "
                                        >
                                            {{ user_warehouse_inventories[product.selected_variation_id]?.stock_balance }}
                                        </div>
                                        <div
                                            v-else-if="
                                                hasInventory &&
                                                isMatrixProduct &&
                                                !isLoadingWarehouseInventories &&
                                                showMinimalistInventory(product.getSelectedVariation())
                                            "
                                            class="item-in-stock"
                                            :class="isInStock ? 'success' : 'danger'"
                                        >
                                            {{
                                                isInStock ? productTranslations.in_stock : productTranslations.out_of_stock
                                            }}
                                        </div>
                                        <span
                                            v-if="
                                                getProductAllowBackorder(product) &&
                                                showMinimalistBackorder(product.getSelectedVariation())
                                            "
                                            class="item-backorder"
                                        >
                                            {{ productTranslations.backorder }}
                                        </span>
                                    </template>
                                </div>
                                <div class="item-name">
                                    {{
                                        (product?.getSelectedVariation &&
                                        product.getSelectedVariation() &&
                                        product.getSelectedVariation().name &&
                                        product.getSelectedVariation().name.trim() !== '')
                                            ? product.getSelectedVariation().name
                                            : product?.name
                                    }}
                                </div>

                                <div
                                    v-if="(showPrice && canReadPrices) || selectedProduct?.has_related_bundles"
                                    class="item-price"
                                >
                                    <template v-if="showPrice && canReadPrices">
                                    <bo-price-display
                                        :price="roundToDecimal(addOrRemoveTaxIfNeeded(product.getCurrentPrice()))"
                                        :original-price="roundToDecimal(addOrRemoveTaxIfNeeded(product.getOriginalPrice()))"
                                        :discount-steps="getLineDiscountSteps()"
                                        :currency-symbol="currencySymbol"
                                        :tax-label="
                                            product.tax_class && !taxExempted && product.tax_class.visible && !hideLineItemTax
                                                ? product.tax_class.name
                                                : null
                                        "
                                        :point="point"
                                        :show-point="pointConfig != null && pointConfig != false"
                                    ></bo-price-display>
                                    </template>
                                    <bo-bundle-deal-link
                                        v-if="selectedProduct?.has_related_bundles"
                                        :label="catalogTranslations?.available_in_bundle_deal ?? 'Available in a bundle deal'"
                                        @click="$emit('open_related_bundles', selectedProduct)"
                                    ></bo-bundle-deal-link>
                                </div>
                                <!-- pack size, shipping class, weight, dimensional sum and volume sit below the price -->
                                <bo-product-specs
                                    :items="productSpecs(product)"
                                    :title="productTranslations.specification ?? 'Specification'"
                                ></bo-product-specs>
                                <bo-radio
                                    v-if="!isMatrixProduct"
                                    :value="product.selected_uom"
                                    :options="productUomOptions"
                                    :labels="productUomLabels"
                                    @change="changeSelectedUom"
                                >
                                </bo-radio>
                                <bo-ta-pricing-deals
                                    v-if="!isMatrixProduct && product.type == 'variable'"
                                    :volume-tiers="tradeAgreementPrices"
                                    :display-price="addOrRemoveTaxIfNeeded"
                                    :volume-loading="volumeTiersLoading"
                                ></bo-ta-pricing-deals>

                                <div
                                    v-if="!isMatrixProduct"
                                    class="quantity"
                                >
                                    <div class="mobile-uom">
                                        <!-- <bo-numeric-counter
                                        v-if="product.type == 'bundle'"
                                        v-model="product.quantity"
                                        :editable="true"
                                    ></bo-numeric-counter> -->
                                        <bo-numeric-counter
                                            v-model="product.quantity"
                                            :editable="true"
                                            :quantityDecimalPlace="quantityDecimalPlace"
                                            @change="queueQuantityPriceReload"
                                        ></bo-numeric-counter>
                                        <div
                                            v-if="showPrice && canReadPrices"
                                            class="total"
                                        >
                                            {{ productTranslations.total }}
                                            <!-- <bo-cell-currency
                                            v-if="product.type == 'bundle'"
                                            :input-style="['text-14', 'text-medium']"
                                            :value="product.getDisplayTotal()"
                                            :currency="currencySymbol"
                                        >
                                        </bo-cell-currency> -->
                                            <bo-cell-currency
                                                :input-style="['text-14', 'text-medium']"
                                                :value="getProductTotal(true)"
                                                :currency="currencySymbol"
                                            >
                                            </bo-cell-currency>
                                        </div>
                                    </div>
                                </div>

                                <div class="button-container">
                                    <div
                                        v-if="isMatrixProduct"
                                        class="btn-matrix"
                                        @click="productMatrixAddToCart = true"
                                    >
                                        {{ productTranslations.add_to_cart }}
                                    </div>
                                    <div
                                        v-if="isMatrixProduct & showPrice && canReadPrices"
                                        class="total"
                                    >
                                        {{ productTranslations.total }}
                                        <bo-cell-currency
                                            :input-style="['text-14', 'text-medium']"
                                            :value="matrixTotal"
                                            :decimal="2"
                                            :currency="currencySymbol"
                                        >
                                        </bo-cell-currency>
                                    </div>
                                    <div
                                        v-if="!isMatrixProduct"
                                        class="btn-rounded text-medium desktop-add-to-cart"
                                        :class="{ disabled: priceRefreshing }"
                                        @click="addToCart"
                                    >
                                        {{ productTranslations.add_to_cart }}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div>
                        <div
                            v-if="showPrice"
                            v-cloak
                            class="floating-footer theme-tertiary-bg-color"
                        >
                            <div class="floating-middle-footer">
                                <div class="left-panel">
                                    <!-- Keep the panel as the flex spacer: the footer is justify-content:space-between,
                                         so removing it would slide the action button to the left. -->
                                    <template v-if="canReadPrices">
                                        <div class="total text-12">{{ productTranslations.total }}</div>
                                        <div>
                                            <bo-cell-currency
                                                :input-style="['text-16', 'text-medium']"
                                                :value="getProductTotal(true)"
                                                :currency="currencySymbol"
                                            >
                                            </bo-cell-currency>
                                        </div>
                                    </template>
                                </div>

                                <div
                                    v-if="!this.cartItemId"
                                    class="btn-rounded btn-primary text-medium mobile-add-to-cart"
                                    :class="{ disabled: priceRefreshing }"
                                    @click="addToCart"
                                >
                                    {{ productTranslations.add_to_cart }} ({{ product.quantity }})
                                </div>
                                <div
                                    v-else-if="this.cartItemId"
                                    class="btn-rounded text-medium desktop-add-to-cart"
                                    :class="{ disabled: priceRefreshing }"
                                    @click="addToCart"
                                >
                                    {{ productTranslations.update_cart }} ({{ product.quantity }})
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <bo-confirmation ref="bo_confirmation"></bo-confirmation>
    </div>
</template>

<script>
import Vue from "vue";
import { v4 as uuidv4 } from "uuid";
import vClickOutside from "v-click-outside";
import productMixin from "@/v2/mixins/products.js";
import messageBagMixin from "@/v2/mixins/message-bag.js";
import cartMixin from "@/v2/mixins/carts.js";
import confirmationMixin from "@/v2/mixins/bo-confirmation.js";
import { Prompt } from "@/v2/models/bo-confirmation.js";
import warehouseInventoryMixin from "@/v2/mixins/warehouse-inventories.js";
import { BundleProduct, BundleComponent, VariableProduct } from "@/v2/models/products";
import { productSpecItems, productSpecValues, productSpecLabels } from "js/frontend/utils/product-specs.js";
import httpMixin from "@/v2/mixins/http.js";
import boDatatableMixin from "@/v2/mixins/bo-datatable.js";
import * as utils from "@/v2/utils/utilities.js";
import { priceVisibilityMixin } from "js/frontend/mixins/price-visibility.js";
import { createPriceRefreshGate, createQuantityReloadQueue } from "js/frontend/utils/price-refresh-gate.js";
import { formatLineDiscountSteps } from "js/frontend/utils/line-discount-label.js";

Vue.use(vClickOutside);

Vue.component("bo-confirmation", require("@/v2/components/bo-confirmation.vue").default);
Vue.component("bo-cell-currency", require("@/v2/components/bo-cell-currency.vue").default);
Vue.component("bo-point", require("js/frontend/components/bo-point.vue").default);
Vue.component("bo-line-discount", require("js/frontend/components/bo-line-discount.vue").default);
Vue.component("bo-price-display", require("js/frontend/components/bo-price-display.vue").default);
Vue.component("bo-product-specs", require("js/frontend/components/bo-product-specs.vue").default);
Vue.component("bo-bundle-deal-link", require("js/frontend/components/bo-bundle-deal-link.vue").default);
Vue.component("bo-ta-pricing-deals", require("js/frontend/components/bo-ta-pricing-deals.vue").default);
Vue.component("bo-radio", require("@/v2/components/bo-radio.vue").default);
Vue.component("bo-numeric-counter", require("js/frontend/components/bo-numeric-counter.vue").default);

export default {
    mixins: [
        productMixin,
        messageBagMixin,
        cartMixin,
        confirmationMixin,
        warehouseInventoryMixin,
        httpMixin,
        boDatatableMixin,
        priceVisibilityMixin,
    ],
    props: {
        id: {
            type: String,
            default: null,
        },
        active: {
            type: Boolean,
            default: false,
        },
        fade: {
            type: Boolean,
            default: true,
        },
        keepAlive: {
            type: Boolean,
            default: false,
        },
        hideCloseButton: {
            type: Boolean,
            default: false,
        },
        bordered: {
            type: Boolean,
            default: false,
        },
        selectedProduct: {
            type: Object,
            default: () => {
                return {};
            },
        },
        cartUrl: {
            type: String,
            default: null,
        },
        productUrl: {
            type: String,
            default: null,
        },
        cartId: {
            type: Number,
            default: null,
        },
        outletId: {
            type: [String, Number],
            default: null,
        },
        cartOutletId: {
            type: [String, Number],
            default: null,
        },
        companyId: {
            type: Number,
            default: null,
        },
        cartMode: {
            type: Boolean,
            default: false,
        },
        cartItem: {
            type: Object,
            default: () => {
                return {};
            },
        },
        tradeAgreementUrl: {
            type: String,
            default: null,
        },
        generalTranslations: {
            type: Object,
            default: () => window.bo?.generalTranslations ?? null,
        },
        catalogTranslations: {
            type: Object,
            default: () => window.bo?.catalogTranslations ?? null,
        },
        currencySymbol: {
            type: String,
            default: () => window.bo?.currencySymbol ?? "RM",
        },
        quantityDecimalPlace: {
            type: Number,
            default: () => window.bo?.lineItemMaxDecimalPlace ?? 0,
        },
    },
    data() {
        return {
            visible: this.active,
            // { productId, formula } from the latest trade agreement price, for the discount label only
            taDiscount: null,
            vcoConfig: {
                handler: this.deactivate,
                middleware: this.ignoreLoadingElement,
            },
            isTaxIncluded: window.bo.taxIncluded,
            isDisplayPricesDuringCartAndCheckoutIncludingTax: window.bo.displayPricesDuringCartAndCheckoutIncludingTax,
            isTaxExempted: window.bo.taxExempted,
            hideLineItemTax: window.bo?.hideLineItemTax ?? false,
            product: null,
            uomMapper: {},
            productImages: {},
            isMatrixProduct: false,
            previousSelectedUom: null,
            pointConfig: window.bo.pointConfig,
            showPrice: false,
            productTranslations: window.bo.productTranslations,
            currentUserWarehouseInventoriesUrl: window.bo.currentUserWarehouseInventoriesUrlLegacy,
            warehouseInventoryUrl: window.bo.warehouseInventoriesUrl,
            hasInventory: window.bo.hasInventory,
            warehouse_inventories: {},
            user_warehouse_inventories: {},
            all_warehouse: {},
            hasWarehouseInventories: false,
            isLoadingWarehouseInventories: true,
            // matrix products: derived from the current-user inventory response; declared here so
            // it is reactive and reset per product (it used to be an undeclared ad-hoc property)
            isInStock: false,
            advancedUomStockQuantity: window.bo.advancedUomStockQuantity,
            // the catalog card's check for volume pricing: false skips the tier request; true shows the
            // badge and a placeholder row until the selected UOM's tiers arrive
            mayHaveVolumePricing: false,
            isVolumePricing: false,
            // volume tier ranges of the selected UOM (was an undeclared, non-reactive property)
            tradeAgreementPrices: [],
            volumeTiersLoading: false,
            weightUnit: window.bo.weightUnit,
            roughStocks: window.bo.roughStocks,
            // true while a trade-agreement price refresh is in flight — gates
            // Add to Cart because addToCartPayload() submits the displayed price (#111920)
            priceRefreshing: false,
            // (variation id, quantity) of the last validated price, so a FAILED refresh
            // can restore a consistent pair instead of reopening the gate on a stale one
            lastPriced: null,
            // id of a product whose load failed, so re-opening that same item retries (#111741)
            failedProductId: null,
        };
    },
    computed: {
        modalId: {
            get() {
                return this.id ?? `bo-popup-modal_${uuidv4()}`;
            },
        },
        firstImage() {
            const sortedEntries = Object.entries(this.productImages).sort(([, a], [, b]) => a.position - b.position);

            const rekeyedObject = {};

            sortedEntries.forEach(([oldKey, value], newIndex) => {
                rekeyedObject[++newIndex] = value;
            });

            const firstKey = Object.keys(rekeyedObject || {})[0];
            return rekeyedObject?.[firstKey]?.source_url || "/assets/graphics/img-product.svg";
        },
        productUomOptions() {
            if (this.product.uom) {
                return Object.values(this.product.uom)
                    .filter((uom) => !uom.hide)
                    .map((uom) => uom.slug);
            } else {
                return [];
            }
        },
        productUomLabels() {
            let temp = {};
            if (this.product.uom) {
                for (const uom of Object.values(this.product.uom)) {
                    temp[uom.slug] = uom.name;
                }
            }

            return temp;
        },
        point: function point() {
            // to trigger point in computed method to re-calculate
            let selectedUom = this.previousSelectedUom;

            // guests have no point config
            if (!this.pointConfig) {
                return 0;
            }
            let tier_multiplier = parseFloat(this.pointConfig.tier_multiplier);
            // if (this.product instanceof BundleProduct) {
            //     let totalPrice = 0;

            //     Object.values(this.product.components).forEach((component) => {
            //         Object.values(component.products).forEach((product) => {
            //             Object.values(product.variations).forEach((variation) => {
            //                 totalPrice += variation.display_unit_price * variation.quantity;
            //             });
            //         });
            //     });

            //     return tier_multiplier * totalPrice * this.pointConfig.conversion_rate;
            // }
            if (this.product instanceof VariableProduct) {
                return tier_multiplier * this.getProductTotal(true) * this.pointConfig.conversion_rate;
            }

            return tier_multiplier * this.getProductTotal(true) * this.pointConfig.conversion_rate;
        },
        showMinimalistStock() {
            return !(
                window.bo.hasCapabilityPhysical ||
                window.bo.hasCapabilitySellable ||
                window.bo.hasCapabilityRoughSellable ||
                !window.bo.hasWarehouse
            );
        },
    },
    watch: {
        active: function (n, o) {
            // document.body.classList.toggle("modal-open");
            this.visible = n;
            if (!n) {
                return;
            }
            if (!this.product && this.selectedProduct?.id === this.failedProductId) {
                // this exact item failed to load last time and is re-opened: selectedProduct did
                // not change, so the watcher will not call getProduct() — retry here (#111741)
                this.getProduct(this.selectedProduct);
            } else if (this.product && this.selectedProduct?.id === this.product.id) {
                // same catalog item re-opened: selectedProduct did not change, so getProduct()
                // will not run — re-price what is shown (gate stays closed until it lands)
                this.reloadTradeAgreementPrice();
            }
        },
        visible: function (n, o) {
            this.$emit(n ? "activate" : "deactivate");
        },
        selectedProduct: function (n, o) {
            this.getProduct(n);
        },
    },
    methods: {
        ...utils,

        activate() {
            this.visible = true;
            document.body.classList.add("modal-open");
            this.$emit("activate");
        },
        deactivate() {
            // a quantity tap queued just before closing must not fire for a product the
            // user is no longer looking at (#111920 review)
            if (this.quantityReloadQueue && this.quantityReloadQueue.cancel()) {
                // the cancelled tap already changed product.quantity, so the displayed price no
                // longer belongs to it — put the pair back in a committable state (#111920 review)
                this.restoreValidatedQuantity(this.selectedVariationId());
            }
            this.visible = false;
            document.body.classList.remove("modal-open");
            this.$emit("deactivate");
        },
        toggle() {
            this.visible ? this.deactivate() : this.activate();
        },
        ignoreLoadingElement(event) {
            if (event.target.classList.contains("loading")) return false;
            return true;
        },

        addOrRemoveTaxIfNeeded(price) {
            if (this.isTaxExempted) {
                if (this.isTaxIncluded && this.product.tax_class) {
                    return price / (1 + this.product.tax_class.percentage / 100);
                } else {
                    return price;
                }
            } else {
                if (
                    !this.product.tax_class ||
                    this.isTaxIncluded == this.isDisplayPricesDuringCartAndCheckoutIncludingTax
                ) {
                    return price;
                } else if (this.isTaxIncluded && !this.isDisplayPricesDuringCartAndCheckoutIncludingTax) {
                    return price / (1 + this.product.tax_class.percentage / 100);
                } else {
                    return price * (1 + this.product.tax_class.percentage / 100);
                }
            }
        },
        roundToDecimal(number, decimal = 2) {
            var formattedResult = parseFloat(number.toFixed(decimal));
            return formattedResult;
        },
        removeTaxIfNeeded(price) {
            if (
                (this.product.tax_class && this.isTaxIncluded) ||
                (this.product.tax_class && this.isTaxIncluded && this.isTaxExempted)
            ) {
                return price / (1 + this.product.tax_class.percentage / 100);
            }
            return price;
        },
        // Trade agreement discount levels for the selected product / variation. The formula comes from the
        // price refresh and is kept apart from the product model, so it is display-only and cannot change pricing.
        getLineDiscountSteps() {
            const productId =
                this.product instanceof VariableProduct ? this.product.selected_variation_id : this.product.id;
            const formula = this.taDiscount && this.taDiscount.productId == productId ? this.taDiscount.formula : null;

            return formatLineDiscountSteps({
                formula,
                unitPrice: this.product.getCurrentPrice(),
                regularPrice: this.product.getOriginalPrice(),
                currencySymbol: this.currencySymbol,
            });
        },
        getTax(price) {
            if (!this.product.tax_class || this.isTaxExempted) {
                return 0;
            }
            if (this.isTaxIncluded) {
                return (
                    (this.product.tax_class.percentage / 100) * (price / (1 + this.product.tax_class.percentage / 100))
                );
            } else {
                return price * (this.product.tax_class.percentage / 100);
            }
        },
        productSpecs(product) {
            return productSpecItems(productSpecValues(product), {
                labels: productSpecLabels(this.productTranslations),
                units: {
                    kg: window.bo.generalTranslations.kg,
                    m: window.bo.generalTranslations.m,
                    m3: window.bo.generalTranslations.m3,
                },
                format: (value) => this.toFixedNumber(value),
            });
        },
        getProduct(product) {
            if (this.quantityReloadQueue) {
                this.quantityReloadQueue.cancel(); // switching product: drop any queued reload
            }
            // #111741: the full-screen veil no longer covers this request, so the previous
            // product must not stay rendered (and purchasable) while the new one loads —
            // v-if="visible && product" leaves only the backdrop until it arrives
            this.product = null;
            // product-scoped state must not leak from the previous product into this one
            this.uomMapper = {};
            this.productImages = {};
            this.isMatrixProduct = false;
            this.showPrice = false;
            this.mayHaveVolumePricing = !!product.has_volume_pricing;
            this.isVolumePricing = this.mayHaveVolumePricing;
            this.tradeAgreementPrices = [];
            this.volumeTiersLoading = false;
            this.warehouse_inventories = {};
            this.user_warehouse_inventories = {};
            this.hasWarehouseInventories = false;
            this.isLoadingWarehouseInventories = true;
            this.isInStock = false;
            this.getData(
                this.productUrl + "/" + product.id,
                null,
                (response) => {
                    let data = response.data;
                    this.product = this.parseProduct(data);
                    this.failedProductId = null;
                    // gate state is per product: a hold ticket left by the previous product
                    // (e.g. its UOM pricing failed) must not keep this one purchase-gated —
                    // simple products are never priced here, so nothing else would clear it
                    this.resetPriceRefreshGate();

                    this.processProductData();
                },
                (error) => {
                    // with product = null the modal (and its close button) is not rendered — close
                    // the backdrop instead of leaving it stuck, and let a re-open of this item retry
                    this.failedProductId = product.id;
                    this.deactivate();
                    this.handleError(error, "Unable to get product", "Unable to get product at the moment.");
                }
            );
        },
        processProductData() {
            let selectedVariation = null;
            // inventory responses below may land after the modal moved on to another product —
            // they must only ever touch the product they were requested for (#111741)
            const requestProduct = this.product;

            if (this.product instanceof VariableProduct) {
                Object.values(this.product.uom).forEach((variation) => {
                    if (!variation.hide && selectedVariation === null) {
                        selectedVariation = variation.slug;
                    }
                });

                this.product.quantity = 1;

                this.product.selectUom(selectedVariation); //Select first uom by default
                // price and volume tiers of the default UOM
                this.reloadTradeAgreement();
                this.isMatrixProduct =
                    this.product.bulk_variation_input && this.product.bulk_variation_input.bulk_variation_form
                        ? true
                        : false;

                Object.values(this.product.variations).forEach((variation) => {
                    this.$set(this.uomMapper, variation.uom.slug, variation.id);
                });
            }

            this.loadAllWarehouseInventories();

            // if (this.getAllWarehouses) {
            //     this.getWarehouses({}, (warehouses) => {
            //         if (warehouses) {
            //             this.all_warehouse = warehouses;
            //         }
            //     });
            // }

            // Gather the inventories using the product_id and Assign to this.userWarehouseInventories

            if (this.currentUserWarehouseInventoriesUrl) {
                this.getCurrentUserWarehouseInventories(
                    {
                        params: { product_id: this.product.id },
                    },
                    (warehouseInventories) => {
                        if (this.product !== requestProduct) {
                            return; // stale: another product is shown now
                        }
                        if (warehouseInventories && !this.advancedUomStockQuantity) {
                            warehouseInventories.forEach((warehouseInventory) => {
                                if (this.product instanceof VariableProduct) {
                                    Object.keys(this.product.variations).forEach((variationId) => {
                                        const variation = this.product.variations[variationId];
                                        if (warehouseInventory.product.conv_rate_group != variation.conv_rate_group) {
                                            return;
                                        }
                                        if (variation.id == warehouseInventory.product_id) {
                                            return;
                                        }

                                        let stockBalance = this.getStockBalance(warehouseInventory, variation);

                                        warehouseInventories.push({
                                            product_id: variation.id,
                                            stock_balance_id: warehouseInventory.stock_balance_id,
                                            stock_balance: stockBalance,
                                            balance: this.roundToDecimal(warehouseInventory.balance / variation.uom.conversion_rate),
                                            physical_balance: this.roundToDecimal(
                                                warehouseInventory.physical_balance / variation.uom.conversion_rate
                                            ),
                                        });
                                    });
                                } else if (this.product instanceof BundleProduct) {
                                    Object.values(this.product.components).forEach((component) => {
                                        Object.values(component.products).forEach((product) => {
                                            Object.values(product.variations).forEach((variation) => {
                                                if (variation.parent_id == warehouseInventory.product_parent_id) {
                                                    let stockBalance = this.getStockBalance(
                                                        warehouseInventory,
                                                        variation
                                                    );

                                                    warehouseInventories.push({
                                                        product_id: variation.id,
                                                        stock_balance: stockBalance,
                                                        balance: this.roundToDecimal(
                                                            warehouseInventory.balance / variation.uom.conversion_rate
                                                        ),
                                                        physical_balance: this.roundToDecimal(
                                                            warehouseInventory.physical_balance /
                                                            variation.uom.conversion_rate
                                                        ),
                                                    });
                                                }
                                            });
                                        });
                                    });
                                }
                            });
                        }
                        this.boProcessRecords(this.user_warehouse_inventories, warehouseInventories, "product_id");
                        if (this.isMatrixProduct) {
                            this.isInStock = Object.values(warehouseInventories).some((warehouseInventory) => {
                                return warehouseInventory.balance > 0;
                            });
                        }
                        if (Object.keys(this.user_warehouse_inventories).length > 0) {
                            this.hasWarehouseInventories = true;
                        } else {
                            this.hasWarehouseInventories = false;
                        }
                        this.isLoadingWarehouseInventories = false;
                    },
                    () => {
                        // #111887: the stock badge is gated on this flag — a failed request must fall
                        // back to the pre-existing rendering, not hide the badge for good
                        if (this.product !== requestProduct) {
                            return; // stale: another product is shown now
                        }
                        this.isLoadingWarehouseInventories = false;
                    }
                );
            } else {
                this.isLoadingWarehouseInventories = false;
            }

            // change image
            this.updateProductImages(this.uomMapper[selectedVariation]);
        },
        reloadTradeAgreementPrice(product = null, quantity = null) {
            let productId =
                this.product instanceof VariableProduct ? this.product.selected_variation_id : this.product.id;
            if (product) {
                productId = product.id;
            }
            quantity = quantity ?? this.product.quantity;
            // the modal can close and re-open on another product while this request is
            // in flight — a response must only ever write into the product it was for
            const requestProduct = this.product;
            const refreshTicket = this.beginPriceRefresh();
            this.getProductTradeAgreementPrice(
                this.cartUrl,
                productId,
                {
                    quantity: quantity,
                },
                (productPrice) => {
                    if (this.product !== requestProduct) {
                        // the modal moved on to another product, which owns a fresh gate —
                        // this response must neither apply nor touch that gate (#111920)
                        return;
                    }
                    if (!this.settlePriceRefresh(refreshTicket)) {
                        // a newer refresh superseded this response (responses apply in
                        // arrival order) — discard the stale price (#111920)
                        return;
                    }
                    this.lastPriced = { productId: productId, quantity: quantity };
                    this.showPrice = true;

                    if (productPrice) {
                        if (this.product instanceof VariableProduct) {
                            this.product.variations[this.product.selected_variation_id].regular_price =
                                productPrice.regular_price;
                            this.product.variations[this.product.selected_variation_id].sale_price =
                                productPrice.sale_price;
                        } else {
                            this.product.regular_price = productPrice.regular_price;
                            this.product.sale_price = productPrice.sale_price;
                        }
                        this.taDiscount = { productId: productId, formula: productPrice.formula ?? null };
                    }
                },
                () => {
                    // failure settles the gate (a gate that never settles blocks purchasing), but
                    // reopening it against an inconsistent price/quantity/variation would let a
                    // stale price be committed — same handling as the product details page.
                    if (this.product !== requestProduct) {
                        return; // another product's gate now — see success path
                    }
                    if (!this.settlePriceRefresh(refreshTicket)) {
                        return;
                    }
                    this.restoreValidatedQuantity(productId);
                },
                false // background refresh: keep the modal interactive, no full-screen overlay (#111920)
            );
        },
        selectedVariationId() {
            return this.product instanceof VariableProduct ? this.product.selected_variation_id : this.product.id;
        },
        restoreValidatedQuantity(productId) {
            if (this.lastPriced && this.lastPriced.productId === productId) {
                // the displayed price for this variation is still the last validated one —
                // restore its quantity so the pair is consistent and committable
                this.product.quantity = this.lastPriced.quantity;
            } else {
                // no validated price for the CURRENTLY selected variation (e.g. a UOM switch
                // whose pricing failed): hold the purchase gate closed with a pending ticket;
                // the next successful refresh supersedes and clears it
                this.beginPriceRefresh();
            }
        },
        resetPriceRefreshGate() {
            // in-flight responses for the previous product bail on the requestProduct check
            // before settling, so they can neither apply nor touch this new gate
            this.priceRefreshGate = createPriceRefreshGate();
            this.priceRefreshing = false;
            this.lastPriced = null;
        },
        beginPriceRefresh() {
            if (!this.priceRefreshGate) {
                this.priceRefreshGate = createPriceRefreshGate();
            }
            this.priceRefreshing = true;
            return this.priceRefreshGate.begin();
        },
        settlePriceRefresh(ticket) {
            const isLatest = this.priceRefreshGate.settle(ticket);
            this.priceRefreshing = this.priceRefreshGate.refreshing();
            return isLatest;
        },
        queueQuantityPriceReload() {
            if (!this.quantityReloadQueue) {
                this.quantityReloadQueue = createQuantityReloadQueue({
                    begin: () => this.beginPriceRefresh(),
                    settle: (ticket) => this.settlePriceRefresh(ticket),
                    reload: () => this.reloadTradeAgreementPrice(),
                    delayMs: 300,
                });
            }
            this.quantityReloadQueue.tap();
        },
        changeSelectedUom(uom) {
            const requestProduct = this.product; // see processProductData()
            this.previousSelectedUom = this.product.selected_uom;
            this.product.selectUom(uom);

            // only reload TA for variable
            // bundle no need reload, price always follow quantity 0
            if (this.product instanceof VariableProduct) {
                this.reloadTradeAgreement();
            }
            let modifiedInventory = [];
            if (this.warehouseInventoryUrl) {
                //clear out inventory
                this.warehouse_inventories = {};
                this.getWarehouseInventories(
                    {
                        params: {
                            product_id: this.product.id,
                        },
                    },
                    (warehouseInventories) => {
                        if (this.product !== requestProduct) {
                            return; // stale: another product is shown now
                        }
                        if (warehouseInventories && !this.advancedUomStockQuantity) {
                            warehouseInventories.forEach((warehouseInventory) => {
                                const variation = this.product.variations[this.product.selected_variation_id];
                                //return all child product existing inventories

                                // if (warehouseInventory.product_id == variation.id) {
                                //     return;
                                // }

                                //check if the selected child product has the same conversion group with base uom
                                //if not same, don't take the inventory
                                if (warehouseInventory.product.conv_rate_group != variation.conv_rate_group) {
                                    return;
                                }

                                //if same calculate conversion
                                modifiedInventory.push({
                                    id: warehouseInventory.id,
                                    product_id: variation.id,
                                    product: variation,
                                    batch_id: warehouseInventory.batch_id,
                                    conv_rate_group: warehouseInventory.conv_rate_group,
                                    stock_balance: window.bo.productTranslations.stock_balance
                                        .replace(
                                            ":sellable",
                                            this.roundToDecimal(warehouseInventory.balance / variation.uom.conversion_rate)
                                        )
                                        .replace(
                                            ":physical",
                                            this.roundToDecimal(warehouseInventory.physical_balance / variation.uom.conversion_rate)
                                        ),
                                    balance: this.roundToDecimal(warehouseInventory.balance / variation.uom.conversion_rate),
                                    physical_balance: this.roundToDecimal(
                                        warehouseInventory.physical_balance / variation.uom.conversion_rate
                                    ),
                                    warehouse: {
                                        location_code: warehouseInventory.warehouse.location_code,
                                    },
                                });
                            });
                            this.warehouse_inventories = modifiedInventory;
                        } else {
                            this.warehouse_inventories = warehouseInventories;
                        }
                    },
                    null,
                    false // UOM switch inventory refresh: no full-screen overlay (#111920)
                );
            }

            // handling the variation switching for inventory stock_balance while advancedUomStockQuantity settings is disabled
            if (this.currentUserWarehouseInventoriesUrl && this.advancedUomStockQuantity) {
                this.getCurrentUserWarehouseInventories(
                    {
                        params: { product_id: this.product.id },
                    },
                    (warehouseInventories) => {
                        if (this.product !== requestProduct) {
                            return; // stale: another product is shown now
                        }
                        this.boProcessRecords(this.user_warehouse_inventories, warehouseInventories, "product_id");
                        // Checking if the selected variation product already exists in the user_warehouse_inventories
                        const isProductPresent = Object.values(this.user_warehouse_inventories).some(
                            (obj) => obj.product_id == this.product.selected_variation_id
                        );
                        const inventory = Object.values(this.user_warehouse_inventories)[0];
                        if (Object.keys(this.user_warehouse_inventories).length > 0 && isProductPresent) {
                            this.hasWarehouseInventories = true;
                        } else {
                            this.hasWarehouseInventories = false;
                            this.isLoadingWarehouseInventories = false;
                        }
                    },
                    null,
                    false // UOM switch inventory refresh: no full-screen overlay (#111920)
                );
            }

            // change image
            this.updateProductImages(this.uomMapper[uom]);
        },
        updateProductImages(productId) {
            this.productImages = {};
            let images = [];
            let count = 1;
            if (productId != null) {
                if (this.product instanceof BundleProduct) {
                } else {
                    Object.values(this.product.variations).forEach((variation) => {
                        if (productId == variation.id) {
                            Object.values(variation.images).forEach((image) => {
                                if (!images.includes(image.source_url)) {
                                    this.$set(this.productImages, count++, image);
                                    images.push(image.source_url);
                                }
                            });
                        }
                    });
                }
            }

            Object.values(this.product.images).forEach((image) => {
                if (!images.includes(image.source_url)) {
                    this.$set(this.productImages, count++, image);
                    images.push(image.source_url);
                }
            });

            if (this.product instanceof BundleProduct) {
            } else {
                Object.values(this.product.variations).forEach((variation) => {
                    if (productId != variation.id) {
                        Object.values(variation.images).forEach((image) => {
                            if (!images.includes(image.source_url)) {
                                this.$set(this.productImages, count++, image);
                                images.push(image.source_url);
                            }
                        });
                    }
                });
            }
        },
        reloadTradeAgreement() {
            this.reloadVolumeTiers();
            this.reloadTradeAgreementPrice();
        },
        // a previous product's request may still land: it must not fill in this product's tiers
        discardPendingResponse(gate) {
            if (gate) {
                gate.settle(gate.begin());
            }
        },
        reloadVolumeTiers() {
            if (!this.mayHaveVolumePricing || !this.canReadPrices) {
                this.discardPendingResponse(this.volumeTiersGate);
                this.tradeAgreementPrices = [];
                this.isVolumePricing = false;
                return;
            }
            // a late response for a previous product or UOM must not overwrite the current one
            if (!this.volumeTiersGate) {
                this.volumeTiersGate = createPriceRefreshGate();
            }
            const tiersTicket = this.volumeTiersGate.begin();
            this.volumeTiersLoading = true;
            this.getTradeAgreementPrices(
                this.cartUrl,
                this.product instanceof VariableProduct ? this.product.selected_variation_id : this.product.id,
                null,
                (tradeAgreementPrices) => {
                    if (!this.volumeTiersGate.settle(tiersTicket)) {
                        return;
                    }
                    this.volumeTiersLoading = false;
                    if (tradeAgreementPrices.length > 1) {
                        this.isVolumePricing = true;
                    } else {
                        this.isVolumePricing = false;
                    }
                    if (tradeAgreementPrices.length > 0) {
                        this.tradeAgreementPrices = tradeAgreementPrices;
                    } else {
                        this.tradeAgreementPrices = [];
                    }
                },
                () => {
                    if (!this.volumeTiersGate.settle(tiersTicket)) {
                        return;
                    }
                    this.volumeTiersLoading = false;
                    this.isVolumePricing = false;
                    this.tradeAgreementPrices = [];
                },
                false // display-only volume-pricing list: no full-screen overlay (#111920)
            );
        },
        getProductTotal(showTax = false) {
            let quantity = this.product.quantity ?? 1;

            if (showTax) {
                return this.addOrRemoveTaxIfNeeded(this.product.getCurrentPrice()) * quantity;
            } else {
                return this.product.getCurrentPrice() * quantity;
            }
        },
        addToCart() {
            // addToCartPayload() submits the displayed price; while a price refresh is
            // in flight that price may be stale, so the click is ignored (#111920).
            // The buttons carry the matching visual disabled state via priceRefreshing.
            if (this.priceRefreshing) {
                return;
            }
            if (this.product) {
                if (!this.validateProduct(this.product)) {
                    this.displayMessages();
                    return;
                }

                const prompt = new Prompt(
                    () => {
                        this.postAddToCart(
                            this.cartUrl,
                            this.addToCartPayload(),
                            //Save product...
                            () => {
                                this.deactivate();
                                this.$emit(
                                    "show_snackbar",
                                    window.bo.productTranslations.quick_added_to_cart.replace(
                                        ":value",
                                        '<span style="color: #007AFF; font-weight: 600;">1</span>'
                                    )
                                );

                                // if (!this.cartItemId || this.cartItemId === null) {
                                //     alert(
                                //         window.bo.productTranslations.product_added_to_cart,
                                //         window.bo.productTranslations.product_added
                                //     );
                                // } else {
                                //     const prompt = new Prompt(
                                //         () => {
                                //             window.location.assign(this.cartUrl);
                                //         },
                                //         () => {
                                //             window.location.assign(this.cartUrl);
                                //         },
                                //         window.bo.productTranslations.cart_updated,
                                //         window.bo.productTranslations.product_updated_in_cart,
                                //         window.bo.generalTranslations.proceed
                                //     );
                                //     this.boPromptIfNeeded(prompt, true);
                                // }
                            },
                            (error) => {
                                if (error.response.data.code == "prevent_adding_to_cart_error_rough_sellable") {
                                    alert(window.bo.productTranslations.prevent_adding_to_cart_error_rough_sellable);
                                } else if (
                                    error.response.data.code == "backorder_disallowed_stock_insufficient_error"
                                ) {
                                    alert(window.bo.productTranslations.backorder_disallowed_stock_insufficient_error);
                                } else if (error.response.data.code == "component_validation_error") {
                                    alert(window.bo.productTranslations.component_validation_error);
                                } else {
                                    alert(window.bo.productTranslations.add_product_error);
                                }
                            }
                        );
                    },
                    null,
                    window.bo.productTranslations.change_selected_outlet,
                    window.bo.productTranslations.change_selected_outlet_message,
                    window.bo.generalTranslations.proceed,
                    "warning"
                );
                const cartOutletId = this.cartOutletId != null ? String(this.cartOutletId) : null;
                const toPrompt = !!cartOutletId && cartOutletId !== String(this.outletId);
                this.boPromptIfNeeded(prompt, toPrompt);
            }
        },
        addToCartPayload() {
            var cartItems = new Array();
            if (this.product instanceof BundleProduct) {
                var childItems = new Array();
                Object.values(this.product.components).forEach((component, key) => {
                    Object.values(component.products).forEach((product) => {
                        Object.values(product.variations).forEach((variation) => {
                            if (variation.quantity != 0) {
                                childItems.push({
                                    product_id: variation.id,
                                    component_id: component.id,
                                    quantity: variation.quantity,
                                    subtotal: this.product.per_item_pricing ? variation.subtotal : 0,
                                    subtotal_tax: variation.subtotal_tax,
                                    total: this.product.per_item_pricing ? variation.total : 0,
                                    total_tax: variation.total_tax,
                                });
                            }
                        });
                    });
                });
                if (childItems.length != 0) {
                    let subtotal = this.product.per_item_pricing
                        ? this.product.getSubtotalForSubmission()
                        : this.removeTaxIfNeeded(this.product.getSubtotal());
                    let total = this.product.per_item_pricing
                        ? this.product.getTotalForSubmission()
                        : this.removeTaxIfNeeded(this.product.getTotal());
                    let subtotal_tax = this.product.per_item_pricing
                        ? this.product.getSubtotalTax()
                        : this.getTax(this.product.getSubtotal());
                    let total_tax = this.product.per_item_pricing
                        ? this.product.getTotalTax()
                        : this.getTax(this.product.getTotal());
                    cartItems.push({
                        product_id: this.product.id,
                        quantity: this.product.quantity,
                        subtotal: subtotal,
                        subtotal_tax: subtotal_tax,
                        total: total,
                        total_tax: total_tax,
                        child_items: childItems,
                    });
                }
            } else if (this.product instanceof VariableProduct) {
                cartItems.push({
                    product_id: this.product.selected_variation_id,
                    component_id: null,
                    quantity: this.product.quantity,
                    subtotal: this.removeTaxIfNeeded(this.product.getSubtotal()),
                    subtotal_tax: this.isTaxExempted ? 0 : this.getTax(this.product.getSubtotal()),
                    total: this.removeTaxIfNeeded(this.getProductTotal()),
                    total_tax: this.isTaxExempted ? 0 : this.getTax(this.getProductTotal()),
                });
            }
            return {
                id: this.cartId,
                outlet: this.outletId,
                cart_item_id: this.cartItemId,
                items: cartItems,
            };
        },
        validateProduct(product) {
            if (product.getQuantity() < 1) {
                this.messageBag.add(
                    "quantity_validation_error",
                    window.bo.productTranslations.quantity_validation_error
                );
            }

            // if (product instanceof BundleProduct) {
            //     if (!this.validateBundle()) {
            //         this.messageBag.add(
            //             "component_validation_error",
            //             window.bo.productTranslations.component_validation_error
            //         );
            //     }
            //     for (const component of Object.values(product.components)) {
            //         if (!this.validateComponent(component)) {
            //             this.messageBag.add(
            //                 "component_validation_error",
            //                 window.bo.productTranslations.component_validation_error
            //             );
            //             break;
            //         }
            //     }
            // }

            if (product instanceof VariableProduct) {
                if (product.selected_variation_id == null) {
                    if (product.selected_uom != null) {
                        console.log(window.bo.productTranslations.variation_not_exist);
                        console.log(this.selected_uom);
                        console.log(product);
                        this.messageBag.add("variation_uom_error", window.bo.productTranslations.variation_uom_error);
                    }
                    this.messageBag.add("uom_selected_error", window.bo.productTranslations.uom_selected_error);
                }
            }

            return this.messageBag.isEmpty();
        },
        // If for the product has no inventory, handling based different user capabilities
        noInventoryTextHandler() {
            if (!window.bo.hasWarehouse) return "";
            if (window.bo.hasCapabilityPhysical) return window.bo.productTranslations.physical;
            if (window.bo.hasCapabilitySellable) return window.bo.productTranslations.sellable;
            if (window.bo.hasCapabilityRoughSellable) return window.bo.roughStockText;
            return window.bo.productTranslations.out_of_stock;
        },
        getStockBalance(warehouseInventory, variation) {
            if (window.bo.hasCapabilityPhysical) {
                let sellable = this.roundToDecimal(warehouseInventory.balance / variation.uom.conversion_rate);
                let physical = this.roundToDecimal(warehouseInventory.physical_balance / variation.uom.conversion_rate);
                return sellable + "s / " + physical + "p";
            }
            if (window.bo.hasCapabilitySellable) {
                return window.bo.productTranslations.amount_in_stock.replace(
                    ":amount",
                    this.roundToDecimal(warehouseInventory.balance / variation.uom.conversion_rate)
                );
            }
            if (window.bo.hasCapabilityRoughSellable) {
                let roughStockMessage = "";

                for (let i = 0; i < this.roughStocks.length; i++) {
                    let balance = this.roundToDecimal(warehouseInventory.balance / variation.uom.conversion_rate);
                    if (this.roughStocks[i]["comparison"] == ">") {
                        if (balance > this.roughStocks[i]["quantity"]) {
                            roughStockMessage = this.roughStocks[i]["display_as"];
                            break;
                        }
                    } else if (this.roughStocks[i]["comparison"] == "<") {
                        if (balance < this.roughStocks[i]["quantity"]) {
                            roughStockMessage = this.roughStocks[i]["display_as"];
                            break;
                        } else {
                            roughStockMessage = balance + " in Stock";
                            break;
                        }
                    }
                }

                return roughStockMessage;
            }

            if (warehouseInventory.balance > 0) return window.bo.productTranslations.in_stock;
            return window.bo.productTranslations.out_of_stock;
        },
        getProductAllowBackorder(product) {
            if (!window.bo.hasWarehouse) return false;
            if (product.type == "variable") {
                if (product.selected_variation_id) {
                    return product.getSelectedVariation().allow_backorders;
                } else if (product.default_variation) {
                    return product.default_variation.allow_backorders;
                } else if (product.base_variation) {
                    return product.base_variation.allow_backorders;
                }
            } else if (product.type == "variation") {
                if (this.product instanceof BundleProduct) {
                    return product.allow_backorders && !this.product.disallow_children_backorders;
                }
                return product.allow_backorders;
            } else if (product.type == "bundle") {
                return product.allow_backorders;
            }
            return false;
        },
        productInventoryExists(productId) {
            return this.user_warehouse_inventories[productId] == null ? false : true;
        },
        productInventorySufficient(productId) {
            if (this.user_warehouse_inventories[productId].balance <= 0) {
                return false;
            }
            return true;
        },
        showMinimalistInventory(product) {
            if (this.isMatrixProduct === true) {
                return (
                    // don't show when product out of stock and backorder allowed
                    !(this.getProductAllowBackorder(product) === true && !this.isInStock)
                );
            } else {
                return (
                    this.showMinimalistStock === false ||
                    // don't show when product out of stock and backorder allowed
                    !(
                        this.getProductAllowBackorder(product) === true &&
                        (!this.productInventoryExists(product.id) ||
                            this.user_warehouse_inventories[product.id].stock_balance_id === "out_of_stock")
                    )
                );
            }
        },
        showMinimalistBackorder(product) {
            if (this.isMatrixProduct === true) {
                return (
                    // don't show when product out of stock and backorder allowed
                    !(this.getProductAllowBackorder(product) === true && this.isInStock)
                );
            } else if (product.type == "bundle") {
                if (!this.getBundleInStock && this.getProductAllowBackorder(product) === true) {
                    return true;
                }
            } else {
                return (
                    this.showMinimalistStock === false ||
                    // don't show when product in stock and backorder allowed
                    !(
                        this.getProductAllowBackorder(product) === true &&
                        this.productInventoryExists(product.id) &&
                        this.user_warehouse_inventories[product.id].stock_balance_id === "in_stock"
                    )
                );
            }
        },
        retrieveBundleVariationInventories() {
            // Getting the inventories for selected component's variation products
            if (Object.values(this.components).length > 0) {
                for (const component of Object.values(this.components)) {
                    const products = component.products;
                    for (const product of Object.values(products)) {
                        const variations = product.variations;
                        for (const variation of Object.values(variations)) {
                            if (this.currentUserWarehouseInventoriesUrl) {
                                const requestProduct = this.product; // see processProductData()
                                this.getCurrentUserWarehouseInventories(
                                    {
                                        params: { product_id: variation.id },
                                    },
                                    (warehouseInventories) => {
                                        if (this.product !== requestProduct) {
                                            return; // stale: another product is shown now
                                        }
                                        let temp = {};
                                        this.boProcessRecords(temp, warehouseInventories, "product_id");
                                        this.user_warehouse_inventories = Object.assign(
                                            this.user_warehouse_inventories,
                                            temp
                                        );
                                        if (Object.keys(this.user_warehouse_inventories).length > 0) {
                                            this.hasWarehouseInventories = true;
                                        } else {
                                            this.hasWarehouseInventories = false;
                                            this.isLoadingWarehouseInventories = false;
                                        }
                                    }
                                );
                            }
                        }
                    }
                }
            }
        },
        getStockClass(productId, user_warehouse_inventories) {
            if (user_warehouse_inventories[productId] == null) return "";

            const stockBalance = user_warehouse_inventories[productId]?.stock_balance;
            const stockBalanceId = user_warehouse_inventories[productId]?.stock_balance_id;
            const balance = user_warehouse_inventories[productId]?.balance;
            const physicalBalance = user_warehouse_inventories[productId]?.physical_balance;

            switch (true) {
                case stockBalanceId == "out_of_stock" || stockBalance.includes("Less than"):
                    return "danger";
                case balance <= 0 && physicalBalance <= 0:
                    return "danger";
                case balance <= 0 && physicalBalance > 0:
                    return "warning";
                case balance > 0 && physicalBalance <= 0:
                    return "warning";
                case balance > 0 && physicalBalance > 0:
                    return "success";
                default:
                    return "";
            }
        },
        loadAllWarehouseInventories() {
            if (this.warehouseInventoryUrl) {
                let modifiedInventory = [];
                const requestProduct = this.product; // see processProductData()
                this.getWarehouseInventories(
                    {
                        params: {
                            product_id: this.product.id,
                        },
                    },
                    (warehouseInventories) => {
                        if (this.product !== requestProduct) {
                            return; // stale: another product is shown now
                        }
                        if (warehouseInventories && !this.advancedUomStockQuantity) {
                            warehouseInventories.forEach((warehouseInventory) => {
                                const variation = this.product.variations[this.product.selected_variation_id];
                                // if (warehouseInventory.product_id == variation.id) {
                                //     return;
                                // }

                                //check if the selected child product has the same conversion group with base uom
                                //if not same, don't take the inventory
                                if (warehouseInventory.product.conv_rate_group != variation.conv_rate_group) {
                                    return;
                                }

                                //if same calculate conversion
                                modifiedInventory.push({
                                    id: warehouseInventory.id,
                                    product_id: variation.id,
                                    product: variation,
                                    batch_id: warehouseInventory.batch_id,
                                    conv_rate_group: warehouseInventory.conv_rate_group,
                                    stock_balance:
                                        this.roundToDecimal(warehouseInventory.balance / variation.uom.conversion_rate) +
                                        "s / " +
                                        this.roundToDecimal(warehouseInventory.physical_balance / variation.uom.conversion_rate) +
                                        "p",
                                    balance: this.roundToDecimal(warehouseInventory.balance / variation.uom.conversion_rate),
                                    physical_balance: this.roundToDecimal(
                                        warehouseInventory.physical_balance / variation.uom.conversion_rate
                                    ),
                                    warehouse: {
                                        location_code: warehouseInventory.warehouse.location_code,
                                    },
                                });
                            });
                            this.warehouse_inventories = modifiedInventory;
                        } else {
                            this.warehouse_inventories = warehouseInventories;
                        }
                    }
                );
            }
        },
    },
};
</script>
